import { alpha, Box, Grid, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { IsSmallScreen } from "../../../utils/CommonValues";
import CustomContainer from "../../container";
import Banners from "../banners";
import BestReviewedItems from "../best-reviewed-items";
import Coupons from "../coupons";
import FeaturedCategories from "../featured-categories";
import NewArrivalStores from "../new-arrival-stores";
import PopularItemsNearby from "../popular-items-nearby";
import RunningCampaigns from "../running-campaigns";
import SpecialFoodOffers from "../special-food-offers";
import Stores from "../stores";
import VisitAgain from "../visit-again";
import PromotionalBanner from "../PromotionalBanner";
import LoveItem from "../love-item";
import useGetOtherBanners from "../../../api-manage/hooks/react-query/useGetOtherBanners";
import PharmacyStaticBanners from "./pharmacy/pharmacy-banners/PharmacyStaticBanners";
import { useSelector } from "react-redux";
import OrderDetailsModal from "../../order-details-modal/OrderDetailsModal";
import { getToken } from "../../../helper-functions/getToken";
import { getCurrentModuleId } from "../../../helper-functions/getCurrentModuleType";
import { styled } from "@mui/material/styles";

const menus = ["All", "Beauty", "Bread & Juice", "Drinks", "Milks"];

// Modern Section Wrapper with better spacing and visual hierarchy
const SectionWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}));

// Hero Section (First section after search)
const HeroSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

// Content Section with subtle background
const ContentSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.3)
    : alpha(theme.palette.primary.main, 0.02),
  borderRadius: "24px",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderRadius: "16px",
  },
}));

// Promotional Section
const PromotionalSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const Grocery = (props) => {
  const { configData } = props;
  const theme = useTheme();
  const token = getToken();
  const { orderDetailsModalOpen, orderInformation } = useSelector(
    (state) => state.utilsData
  );
  const { data, refetch, isLoading } = useGetOtherBanners();
  useEffect(() => {
    refetch();
  }, []);
  const visitedStores = localStorage.getItem("visitedStores")
    ? JSON.parse(localStorage.getItem("visitedStores"))?.length > 0
      ? JSON.parse(localStorage.getItem("visitedStores"))?.filter(
          (item) => item?.module_id === getCurrentModuleId()
        )
      : []
    : [];
  return (
    <Box sx={{ width: "100%", backgroundColor: theme.palette.background.default }}>
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {/* HERO SECTION - Featured Categories */}
        <Grid item xs={12}>
          <HeroSection>
            <CustomContainer>
              <FeaturedCategories configData={configData} />
            </CustomContainer>
          </HeroSection>
        </Grid>

        {/* QUICK ACCESS SECTION - Visit Again */}
        <Grid item xs={12}>
          <SectionWrapper>
            {IsSmallScreen() ? (
              <VisitAgain configData={configData} visitedStores={visitedStores} />
            ) : (
              <CustomContainer>
                <VisitAgain configData={configData} visitedStores={visitedStores} />
              </CustomContainer>
            )}
          </SectionWrapper>
        </Grid>

        {/* POPULAR ITEMS SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <PopularItemsNearby
                title="Most Popular Items"
                subTitle="We provide best quality & fresh grocery items near your location"
              />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* BANNERS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <PharmacyStaticBanners />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* SPECIAL OFFERS SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <SpecialFoodOffers />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* PROMOTIONAL BANNERS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <Banners />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* BEST REVIEWED SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <BestReviewedItems
                menus={menus}
                title="Best Reviewed Items"
                bannerIsLoading={isLoading}
                info={data}
              />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* RUNNING CAMPAIGNS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <RunningCampaigns />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* RECOMMENDED FOR YOU SECTION */}
        <Grid item xs={12}>
          <ContentSection>
            <CustomContainer>
              <LoveItem />
            </CustomContainer>
          </ContentSection>
        </Grid>

        {/* COUPONS SECTION */}
        <Grid item xs={12}>
          <PromotionalSection>
            {IsSmallScreen() ? (
              <Coupons />
            ) : (
              <CustomContainer>
                <Coupons />
              </CustomContainer>
            )}
          </PromotionalSection>
        </Grid>

        {/* NEW ARRIVALS SECTION */}
        <Grid item xs={12}>
          <SectionWrapper>
            <CustomContainer>
              <NewArrivalStores />
            </CustomContainer>
          </SectionWrapper>
        </Grid>

        {/* PROMOTIONAL BANNER SECTION */}
        <Grid item xs={12}>
          <PromotionalSection>
            <PromotionalBanner bannerData={data} />
          </PromotionalSection>
        </Grid>

        {/* ALL STORES SECTION */}
        <Grid item xs={12}>
          <SectionWrapper sx={{ paddingBottom: { xs: 4, md: 6 } }}>
            <CustomContainer>
              <Stores />
            </CustomContainer>
          </SectionWrapper>
        </Grid>
      {orderDetailsModalOpen && !token && (
        <OrderDetailsModal
          orderDetailsModalOpen={orderDetailsModalOpen}
          orderInformation={orderInformation}
        />
      )}
      </Grid>
    </Box>
  );
};

Grocery.propTypes = {};

export default Grocery;
